import { Injectable } from '@angular/core';

import {
  AnyNode, AllNode, ClauseNode, DecisionModel, EvalContext, Node, Outcome, OutcomeKey, TraceEntry
} from '../models/decision-model';

@Injectable({ providedIn: 'root' })
export class DecisionEngineService {
  evaluate(model: DecisionModel, ctx: EvalContext): { outcome: Outcome; trace: TraceEntry[] } {
    const trace: TraceEntry[] = [];
    const outcomes = model.outcomes;

    const exec = (node: Node): Outcome | boolean => {
      if (typeof node === 'string') {
        trace.push({ type: 'OUTCOME', detail: node });
        return outcomes[node as OutcomeKey] ?? outcomes['NOT_COVERED'];
      }

      // CLAUSE -> boolean
      if ((node as ClauseNode).type === 'CLAUSE') {
        const n = node as ClauseNode;
        const ok = this.evalExpr(n.cond, ctx);
        trace.push({ nodeId: n.id, type: 'CLAUSE', ok, detail: n.cond });
        return ok;
      }

      // ALL -> çocuklar boolean; then/else varsa dallan
      if ((node as AllNode).type === 'ALL') {
        const n = node as AllNode;
        const allOk = n.children.every(c => exec(c) === true);
        trace.push({ nodeId: n.id, type: 'ALL', ok: allOk });
        if (n.then === undefined && n.else === undefined) return allOk;
        if (allOk) return typeof n.then === 'string' ? outcomes[n.then] : exec(n.then!);
        return typeof n.else === 'string' ? outcomes[n.else as OutcomeKey] : exec(n.else!);
      }

      // ANY -> çocuklardan biri true; then/else varsa dallan
      if ((node as AnyNode).type === 'ANY') {
        const n = node as AnyNode;
        const anyOk = n.children.some(c => exec(c) === true);
        trace.push({ nodeId: n.id, type: 'ANY', ok: anyOk });
        if (n.then === undefined && n.else === undefined) return anyOk;
        if (anyOk) return typeof n.then === 'string' ? outcomes[n.then] : exec(n.then!);
        return typeof n.else === 'string' ? outcomes[n.else as OutcomeKey] : exec(n.else!);
      }

      return outcomes['NOT_COVERED'];
    };

    const res = exec(model.tree) as Outcome;
    return { outcome: res, trace };
  }

  // Basit/güçlü evaluator: "A in B" --> "includes" + küçük normalizasyon
  private evalExpr(expr: string, ctx: any): boolean {
    const inToIncludes = (s: string) =>
      s.replace(
        /([A-Za-z0-9_.'"\[\]\(\)\?\:\s\+\-\*\/<>=!&|]+?)\s+in\s+([A-Za-z0-9_.'"\[\]\(\)\?\:\s\+\-\*\/<>=!&|]+)/g,
        (_m, a, b) => `__has(${b}, ${a})`
      );
    const normalize = (s: string) =>
      s.replace(/\band\b/gi, '&&').replace(/\bor\b/gi, '||');

    const prepared = normalize(inToIncludes(expr));

    const __has = (collection: any, item: any) => {
      try {
        if (Array.isArray(collection)) return collection.includes(item ?? '');
        if (collection && typeof collection === 'object')
          return Object.prototype.hasOwnProperty.call(collection, String(item ?? ''));
        return false;
      } catch { return false; }
    };

    const safeCtx = { ...ctx, __has };

    try {
      return !!Function('ctx', `with(ctx){ return (${prepared}); }`)(safeCtx);
    } catch (e) {
      console.error('Eval error:', e, { expr, prepared, ctx });
      return false;
    }
  }
}
